import React, { useEffect, useState } from 'react';
import { Alert, Card, Tab } from 'react-bootstrap';
import { useParams } from 'react-router-dom';

import { ClientTab } from '../../components/Clients/ClientTab';
import { TabsHistory } from '../../../app/components/TabsHistory';
import { PurchaserTab } from '../../components/Purchasers/PurchaserTab';
import { PositionTab } from '../../components/Positions/PositionTab';
import { useNotifications } from '../../../common/hooks/useNotifications';
import { usePokCore } from '../../../common/hooks/usePokCore';
import { GetClientDto } from '../../../common/pokCore/autogenerated/pokApiClient';
import { SymphonyTab } from '../../components/Symphony/SymphonyTab';
import { ContactTab } from '../../components/Contacts/ContactTab';
import { EmailAddressTab } from '../../components/EmailAddress/EmailAddressTab';
import { ClientGroupMembershipTab } from '../../components/ClientGroups/ClientGroupMembershipTab';
import { useTranslation } from '../../../common/hooks/useTranslation';

type ParamTypes = {
  clientId: string;
};

export const ClientView: React.FC = () => {
  const [client, setClient] = useState<GetClientDto>();
  const [refresh, setRefresh] = useState<boolean>(false);
  const [notActiveWarning, setNotActiveWarning] = useState<boolean>(false);

  const { clientId } = useParams<ParamTypes>() as {
    clientId: string;
  };
  const pok = usePokCore();
  const notifications = useNotifications();
  const { t, tk } = useTranslation('clientLng');

  useEffect(() => {
    pok.clients
      .getById(clientId)
      .then(client => {
        setClient(client);
      })
      .catch(errorResponse => {
        notifications.caughtError(errorResponse);
      });
  }, [clientId, notifications, pok.clients, refresh]);

  useEffect(() => {
    if (client && client.active === false) {
      pok.clients
        .checkActivePurchaserPositions(client.id)
        .then(result => {
          if (result) {
            setNotActiveWarning(true);
          } else {
            setNotActiveWarning(false);
          }
        })
        .catch(errorResponse => {
          notifications.caughtError(errorResponse);
        });
    } else {
      setNotActiveWarning(false);
    }
  }, [client, notifications, pok.clients, refresh]);

  const onRefresh = () => {
    setRefresh(!refresh);
  };

  return (
    <Card className="main-card">
      <Card.Body className="m-3">
        <Card.Title className="pb-3">
          <span
            style={
              client?.active === false ? { textDecoration: 'line-through' } : {}
            }
          >
            {t(tk.client.client)} <b>{client?.name}</b>
            {client
              ? `, ${t(tk.common.sysNumberLowerCase)} ${client.systemNumber}`
              : null}
          </span>
        </Card.Title>
        {notActiveWarning && (
          <Alert variant="warning">
            {t(tk.client.notActivePurchasersPositions)}
          </Alert>
        )}
        {client && (
          <TabsHistory
            activeKey="details"
            id="product"
            className="mb-3"
            mountOnEnter={true}
            unmountOnExit={true}
          >
            <Tab eventKey="details" title={t(tk.client.client)} className="m-3">
              <ClientTab client={client} refresh={onRefresh} />
            </Tab>
            <Tab
              eventKey="purchaser"
              title={t(tk.purchaser.purchaser)}
              className="m-3"
            >
              <PurchaserTab client={client} refresh={onRefresh} />
            </Tab>
            <Tab
              eventKey="position"
              title={t(tk.position.positionsShortName)}
              className="m-3"
            >
              <PositionTab client={client} refresh={onRefresh} />
            </Tab>
            <Tab
              eventKey="symphony"
              title={t(tk.client.symphony)}
              className="m-3"
            >
              <SymphonyTab clientId={client.id} />
            </Tab>
            <Tab
              eventKey="contacts"
              title={t(tk.client.contacts)}
              className="m-3"
            >
              <ContactTab clientId={client.id} client={client} />
            </Tab>
            <Tab
              eventKey="emailAddress"
              title={t(tk.client.emailAddresses)}
              className="m-3"
            >
              <EmailAddressTab clientId={client.id} client={client} />
            </Tab>
            <Tab
              eventKey="groupMembership"
              title={t(tk.clientGroup.groupMembership)}
              className="m-3"
            >
              <ClientGroupMembershipTab clientId={client.id} />
            </Tab>
          </TabsHistory>
        )}
      </Card.Body>
    </Card>
  );
};
